import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Badge, Spinner, Button, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { 
  fetchLunchPlanWaivers,
  selectWaivers, 
  selectMeta, 
  selectLoading, 
  selectError
} from '../../store/lunchPlanWaiver/lunchPlanWaiverSlice';


const LunchPlanWaiverListComp = ({ data, viewMode }) => {
  // Always call hooks first
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const waivers = useSelector(selectWaivers);
  const meta = useSelector(selectMeta);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedWaiver, setSelectedWaiver] = useState(null);

  useEffect(() => {
    dispatch(fetchLunchPlanWaivers({ page: currentPage, limit: 10 }));
  }, [dispatch, currentPage]);

  // In profile view, always use the passed-in data prop (already filtered by parent)
  if (viewMode === 'profile') {
    if (!Array.isArray(data) || data.length === 0) {
      // Show nothing if no active data (parent will show form)
      return null;
    }
    // Render only the passed-in data
    return (
      <Container>
        <Row>
          {data.map((waiver) => (
            <Col lg="12" key={waiver.id} className="mb-3">
              <Card className="shadow-sm">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <h5 className="mb-2">
                        Lunch Plan Waiver #{waiver.id}
                        <Badge bg="success" className="ms-2">Active</Badge>
                      </h5>
                      <p className="text-muted mb-0">
                        <small>Submitted: {waiver.fillup_date}</small>
                      </p>
                    </div>
                    <div>
                      <Button
                        variant="info"
                        size="sm"
                        className="me-2"
                        onClick={() => setShowDetailModal(true)}
                      >
                        <i className="icon material-symbols-outlined me-1" style={{ verticalAlign: 'middle' }}>visibility</i>
                        View
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => navigate(`/dashboard/lunch-plan-waiver/edit/${waiver.id}`)}
                      >
                        <i className="icon material-symbols-outlined me-1" style={{ verticalAlign: 'middle' }}>edit</i>
                        Edit
                      </Button>
                    </div>
                  </div>
                  <Row>
                    <Col md="6">
                      <p className="mb-2">
                        <strong>Reason:</strong>
                        <br />
                        {waiver.reason || 'N/A'}
                      </p>
                    </Col>
                    <Col md="6">
                      <div className="mb-3">
                        <p className="mb-2"><strong>Parent Signature:</strong></p>
                        {waiver.parents_signature ? (
                          <div className="border rounded p-2 bg-light mb-1">
                            <img 
                              src={waiver.parents_signature} 
                              alt="Parent Signature" 
                              style={{ maxWidth: '200px', maxHeight: '80px', display: 'block' }}
                            />
                          </div>
                        ) : (
                          <span className="text-muted">N/A</span>
                        )}
                        <small className="text-muted">Date: {waiver.parent_signature_date}</small>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>
    );
  }
  
    const handleViewDetails = (waiver) => {
      setSelectedWaiver(waiver);
      setShowDetailModal(true);
    };
  
    const handleCloseModal = () => {
      setShowDetailModal(false);
      setSelectedWaiver(null);
    };
  
    const formatDate = (dateString) => {
      if (!dateString || dateString === '1899-11-29' || dateString === 'Invalid DateTime') return 'N/A';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };
  
    return (
      <Container>
        <Row>
          <Col sm="12">
            {/* <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <div className="header-title">
                  <h4 className="card-title">Lunch Plan Waivers</h4>
                </div>
                <Link to="/dashboard/lunch-plan-waiver/add">
                  <Button variant="primary" size="sm">
                    <i className="icon material-symbols-outlined me-1" style={{verticalAlign: 'middle'}}>add</i>
                    Create New Waiver
                  </Button>
                </Link>
              </Card.Header>
              <Card.Body> */}
                {loading ? (
                  <div className="text-center p-5">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                  </div>
                ) : error ? (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                ) : waivers.length === 0 ? (
                  <div className="text-center p-5">
                    <p className="text-muted">No lunch plan waivers available</p>
                    <Link to="/dashboard/lunch-plan-waiver/add">
                      <Button variant="primary">Create Your First Waiver</Button>
                    </Link>
                  </div>
                ) : (
                  <>
                    <Row>
                      {waivers.map((waiver) => (
                        <Col lg="12" key={waiver.id} className="mb-3">
                          <Card className="shadow-sm">
                            <Card.Body>
                              <div className="d-flex justify-content-between align-items-start mb-3">
                                <div>
                                  <h5 className="mb-2">
                                    Lunch Plan Waiver #{waiver.id}
                                    {waiver.status === 1 ? (
                                      <Badge bg="success" className="ms-2">Active</Badge>
                                    ) : (
                                      <Badge bg="secondary" className="ms-2">Inactive</Badge>
                                    )}
                                  </h5>
                                  <p className="text-muted mb-0">
                                    <small>Submitted: {formatDate(waiver.fillup_date)}</small>
                                  </p>
                                </div>
                                <div className="d-flex gap-2">
                                  <Button 
                                    variant="info" 
                                    size="sm"
                                    onClick={() => handleViewDetails(waiver)}
                                  >
                                    <i className="icon material-symbols-outlined me-1" style={{verticalAlign: 'middle'}}>visibility</i>
                                    View
                                  </Button>
                                  {waiver.status === 1 && (
                                    <Button 
                                      variant="primary" 
                                      size="sm"
                                      onClick={() => navigate(`/dashboard/lunch-plan-waiver/edit/${waiver.id}`)}
                                    >
                                      <i className="icon material-symbols-outlined me-1" style={{verticalAlign: 'middle'}}>edit</i>
                                      Edit
                                    </Button>
                                  )}
                                </div>
                              </div>
  
                              <Row>
                                <Col md="6">
                                  <p className="mb-2">
                                    <strong>Reason:</strong>
                                    <br />
                                    {waiver.reason || 'N/A'}
                                  </p>
                                </Col>
                                <Col md="6">
                                  <div className="mb-3">
                                    <p className="mb-2"><strong>Parent Signature:</strong></p>
                                    {waiver.parents_signature ? (
                                      <div className="border rounded p-2 bg-light mb-1">
                                        <img 
                                          src={waiver.parents_signature} 
                                          alt="Parent Signature" 
                                          style={{ maxWidth: '200px', maxHeight: '80px', display: 'block' }}
                                        />
                                      </div>
                                    ) : (
                                      <span className="text-muted">N/A</span>
                                    )}
                                    <small className="text-muted">Date: {formatDate(waiver.parent_signature_date)}</small>
                                  </div>
                                  {waiver.supervisor_signature && (
                                    <div className="mb-2">
                                      <p className="mb-2"><strong>Supervisor Signature:</strong></p>
                                      <div className="border rounded p-2 bg-light mb-1">
                                        <img 
                                          src={waiver.supervisor_signature} 
                                          alt="Supervisor Signature" 
                                          style={{ maxWidth: '200px', maxHeight: '80px', display: 'block' }}
                                        />
                                      </div>
                                    </div>
                                  )}
                                </Col>
                              </Row>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
  
                    {/* Pagination */}
                    {meta && meta.last_page > 1 && (
                      <div className="d-flex justify-content-center mt-4">
                        <nav>
                          <ul className="pagination">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button 
                                className="page-link" 
                                onClick={() => setCurrentPage(currentPage - 1)}
                                disabled={currentPage === 1}
                              >
                                Previous
                              </button>
                            </li>
                            {[...Array(meta.last_page)].map((_, index) => (
                              <li 
                                key={index + 1} 
                                className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                              >
                                <button 
                                  className="page-link" 
                                  onClick={() => setCurrentPage(index + 1)}
                                >
                                  {index + 1}
                                </button>
                              </li>
                            ))}
                            <li className={`page-item ${currentPage === meta.last_page ? 'disabled' : ''}`}>
                              <button 
                                className="page-link" 
                                onClick={() => setCurrentPage(currentPage + 1)}
                                disabled={currentPage === meta.last_page}
                              >
                                Next
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                  </>
                )}
              {/* </Card.Body>
            </Card> */}
          </Col>
        </Row>
  
        {/* Detail Modal */}
        <Modal 
          show={showDetailModal} 
          onHide={handleCloseModal}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Lunch Plan Waiver Details
              {selectedWaiver && (
                selectedWaiver.status === 1 ? (
                  <Badge bg="success" className="ms-2">Active</Badge>
                ) : (
                  <Badge bg="secondary" className="ms-2">Inactive</Badge>
                )
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {selectedWaiver && (
              <div>
                <h5 className="mb-3">Waiver Information:</h5>
                <p><strong>Submitted Date:</strong> {formatDate(selectedWaiver.fillup_date)}</p>
                <p><strong>Reason:</strong><br />{selectedWaiver.reason || 'N/A'}</p>
  
                <hr />
  
                <h5 className="mb-3">Signatures:</h5>
                <Row>
                  <Col md="6">
                    <p><strong>Parent Signature:</strong></p>
                    {selectedWaiver.parents_signature ? (
                      <div className="border rounded p-2 bg-light mb-2">
                        <img 
                          src={selectedWaiver.parents_signature} 
                          alt="Parent Signature" 
                          style={{ maxWidth: '100%', maxHeight: '120px', display: 'block' }}
                        />
                      </div>
                    ) : (
                      <p className="text-muted">N/A</p>
                    )}
                    <p><strong>Date:</strong> {formatDate(selectedWaiver.parent_signature_date)}</p>
                  </Col>
                  <Col md="6">
                    <p><strong>Supervisor Signature:</strong></p>
                    {selectedWaiver.supervisor_signature ? (
                      <div className="border rounded p-2 bg-light mb-2">
                        <img 
                          src={selectedWaiver.supervisor_signature} 
                          alt="Supervisor Signature" 
                          style={{ maxWidth: '100%', maxHeight: '120px', display: 'block' }}
                        />
                      </div>
                    ) : (
                      <p className="text-muted">N/A</p>
                    )}
                  </Col>
                </Row>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Close
            </Button>
            {selectedWaiver && selectedWaiver.status === 1 && (
              <Button 
                variant="primary" 
                onClick={() => {
                  handleCloseModal();
                  navigate(`/dashboard/lunch-plan-waiver/edit/${selectedWaiver.id}`);
                }}
              >
                Edit Waiver
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      </Container>
    );
};
export default LunchPlanWaiverListComp;
